import { ColorSelectionOption, ColorSelectionTemplateStep, ConfigurationTemplate, ConfigurationTemplateStepType, CustomQuantitySelectionOption, CustomQuantitySelectionTemplateStep, QuantitySelectionOption, QuantitySelectionTemplateStep, TileSelectionOption, TileSelectionTemplateStep } from '@configuration/wizard/models/wizard-model.interface';
import { WizardModel } from '../wizard-model';
import { Injectable } from '@angular/core';
import { ConfigurationCreationService } from '../../service/configuration-creation.service';
import { OverlayService } from '@shared/overlay/overlay.service';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { Log } from '@shared/debug/log';
import { RouteNavigationService } from '@services/route-navigation.service';
import { CreateProjectService } from '@project/service/create-project.service';
import { DistributionTerminalsAssistantService } from '../../../assistants/distribution-terminals-assistant/distribution-terminals-assistant.service';
import { DistributionTerminalAssistantQuestion, DistributionTerminalAssistantQuestions } from '../../../assistants/distribution-terminals-assistant/distribution-terminal-assistant-questions';
import { ColorMultipleChoiceQuestion, ImageMultipleChoice, MultipleChoiceQuestion, NumberValueQuestion } from '../../../assistants/question/question';
import { assertExhaustiveSwitch } from '@shared/util/ts-utils';

@Injectable()
export class DistributionTerminalsWizardModel extends WizardModel {

  private _questions = new DistributionTerminalAssistantQuestions();

  constructor(
    private routeNavigationService: RouteNavigationService,
    private overlayService: OverlayService,
    private configurationCreationService: ConfigurationCreationService,
    private createProjectService: CreateProjectService,
    private distributionTerminalsAssistantService: DistributionTerminalsAssistantService,
  ) {
    super();
  }

  public type: string = "DISTRIBUTION_TERMINALS";

  protected createConfig(): ConfigurationTemplate {
    return {
      id: "create-project-distribution-terminals",
      type: 'assistant',
      imageUrl:
        "ng-assets/img/assistants/distribution-terminals/distribution-terminals.webp",
      label: `configurationCreation.type.distribution_terminals`,
      steps: [
        {
          type: "meta",
          id: "meta",
          label: "configurationCreation.progress.configuration_data",
        },
        ...this.createQuestionSteps(),
        {
          type: "summary",
          id: "summary",
          label:
            "configuration.assistants.distributionTerminals.question.topic.summary",
          getSummary: () => this._questions.getSummary(),
        },
      ],
    };
  }

  private createQuestionSteps(): ConfigurationTemplateStepType[] {
    return this._questions.list.map((question, index) => {
      const questionType = question.type;
      switch (questionType) {
        case "NumberValue":
          return this.createQuantitySelectionStep(question, index);
        case "MultipleChoice":
          return this.createCustomSelectionStep(question, index);
        case 'ImageMultipleChoice':
          return this.createTileSelectionStep(question, index);
        case 'ColorMultipleChoice':
          return this.createColorSelectionStep(question, index);
        default:
          return assertExhaustiveSwitch(questionType);
      }
    });
  }

  public selectValue(step: number, value: string | number | undefined): void {
    if (step - 1 === this._questions.currentIndex) {
      this._questions.currentQuestion.answer = value;
    }
  }

  public async create(): Promise<void> {
    if (this.creationStarted) {
      return;
    }

    this.creationStarted = true;

    // Do not await this promise so dialog will be closed while project is being created.
    this.overlayService.doWithOverlay(async () => {
      await this.configurationCreationService.prepareNewConfiguration(
        this.metaData,
      );

      try {
        await this.routeNavigationService.toConfigurator();
      } catch (error) {
        Log.error("Could not navigate to configurator: ", error);
        return;
      }

      await this.createProjectService.createFreeMounting();

      const requirements = this._questions.getResult();

      if (requirements) {
        await this.distributionTerminalsAssistantService.addDevices(
          requirements,
        );
      }

      this.creationStarted = false;

    }, OverlayBackground.color);
  }

  public async nextStep(): Promise<void> {
    if (this.currentStepIndex === 0) {
      await super.nextStep();
    } else {
      this._questions.next();
      this._currentStepIndex$.next(this._questions.currentIndex + 1);
    }
  }

  public async previousStep(): Promise<void> {
    if (this._questions.currentIndex === 0) {
      await super.previousStep();
    } else {
      this._questions.back();
      this._currentStepIndex$.next(this._questions.currentIndex + 1);
    }
  }

  private createTileSelectionStep(question: ImageMultipleChoice & DistributionTerminalAssistantQuestion, index: number): TileSelectionTemplateStep {
    return {
      type: "tileSelection",
      id: `question-${index}`,
      label: question.topic,
      title: question.text,
      getOptions: () => this.createTileSelectionOptions(question),
    };
  }

  private createCustomSelectionStep(question: MultipleChoiceQuestion & DistributionTerminalAssistantQuestion, index: number): CustomQuantitySelectionTemplateStep {
    return {
      type: "customQuantitySelection",
      id: `question-${index}`,
      label: question.topic,
      title: question.text,
      getOptions: () => this.createCustomSelectionOptions(question),
    };
  }

  private createColorSelectionStep(question: ColorMultipleChoiceQuestion & DistributionTerminalAssistantQuestion, index: number): ColorSelectionTemplateStep {
    return {
      type: "colorSelection",
      id: `question-${index}`,
      label: question.topic,
      title: question.text,
      getOptions: () => this.createColorSelectionOptions(question),
    }
  }

  private createQuantitySelectionStep(question: NumberValueQuestion & DistributionTerminalAssistantQuestion, index: number): QuantitySelectionTemplateStep {
    return {
      type: "quantitySelection",
      id: `question-${index}`,
      label: question.topic,
      title: question.text,
      info: question.unitText,
      getOptions: (): Promise<QuantitySelectionOption[]> => Promise.resolve([ {
        min: question.minValue,
        max: question.maxValue,
      } ]),
    }
  }

  private createTileSelectionOptions(question: ImageMultipleChoice): Promise<TileSelectionOption[]> {
    return Promise.resolve(question.answers().map(answer => ({
      label: answer.metadata.text,
      id: `${question.type}-${answer.value}`,
      value: answer.value,
      imageUrl: answer.metadata.imageUrl,
      size: 'M',
      skipLabelTranslation: !answer.metadata.translateText,
    })));
  }

  private createCustomSelectionOptions(question: MultipleChoiceQuestion): Promise<CustomQuantitySelectionOption[]> {
    return Promise.resolve(question.answers().map(answer => ({
      value: answer.value,
      text: answer.metadata.text,
    })));
  }

  private createColorSelectionOptions(question: ColorMultipleChoiceQuestion): Promise<ColorSelectionOption[]> {
    return Promise.resolve(question.answers().map(answer => ({
      label: answer.metadata.text,
      id: `${question.type}-${answer.value}`,
      value: answer.value,
      color: answer.metadata.color,
      skipLabelTranslation: !answer.metadata.translateText,
    })));
  }
}
